import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, X, Send, Loader, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// Use relative URL for API calls - works in both development and production
const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:4000'
  : window.location.origin;

const AiChatPanel = ({ scanId, url_name }) => {
  console.log('🤖 AiChatPanel mounted with:', { scanId, url_name });
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation history when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadConversationHistory();
    }
  }, [isOpen]);

  const loadConversationHistory = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-chat/history/${scanId}?limit=5`);
      const data = await response.json();

      if (data.success && data.history.length > 0) {
        const formattedMessages = data.history.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: new Date()
        }));
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Build conversation history for API
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await fetch(`${API_BASE_URL}/api/ai-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scan_id: scanId,
          message: inputMessage,
          conversation_history: conversationHistory
        })
      });

      const data = await response.json();

      if (data.success) {
        const aiMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          tokens: data.tokens_used,
          cost: data.cost_usd,
          cached: data.cached || false
        };
        setMessages(prev => [...prev, aiMessage]);
      } else {
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "Why did we fail critical controls?",
    "What are the most urgent issues to fix?",
    "How can we improve our compliance score?",
    "What frameworks did we fail?"
  ];

  console.log('🎨 AiChatPanel rendering, isOpen:', isOpen);

  // Render to document.body using portal to bypass layout wrappers
  return createPortal(
    <>
      {/* Floating chat button */}
      {!isOpen && (
        <button
          onClick={() => {
            console.log('🔵 AI Chat button clicked');
            setIsOpen(true);
          }}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group hover:scale-105"
          style={{ zIndex: 9999 }}
        >
          <div className="relative">
            <MessageCircle className="w-6 h-6 text-white" />
            <Sparkles className="w-3 h-3 text-yellow-300 absolute -top-0.5 -right-0.5 animate-pulse" />
          </div>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[380px] h-[550px] bg-white rounded-xl shadow-xl flex flex-col border border-gray-300" style={{ zIndex: 9999 }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-t-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base">AI Assistant</h3>
                <p className="text-white/80 text-xs truncate max-w-[180px]">{url_name || 'Compliance Scan'}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors p-1 hover:bg-white/10 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl mx-auto mb-3 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-gray-900 font-semibold text-sm mb-2">Ask me anything!</h4>
                <p className="text-gray-600 text-xs mb-4">I can help you understand your compliance results.</p>

                {/* Suggested questions */}
                <div className="space-y-1.5">
                  {suggestedQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInputMessage(question)}
                      className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-blue-50 text-xs text-gray-700 border border-gray-200 transition-all hover:border-blue-300 hover:shadow-sm"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 ${
                    message.role === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : message.isError
                      ? 'bg-red-50 text-red-900 border border-red-200'
                      : 'bg-white border border-gray-200 shadow-sm'
                  }`}
                >
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none">
                      <ReactMarkdown
                        components={{
                          p: ({ node, ...props }) => (
                            <p className="text-gray-800 text-sm leading-relaxed my-2 first:mt-0 last:mb-0" {...props} />
                          ),
                          strong: ({ node, ...props }) => (
                            <strong className="font-bold text-gray-900" {...props} />
                          ),
                          ul: ({ node, ...props }) => (
                            <ul className="list-disc pl-4 my-2 space-y-1" {...props} />
                          ),
                          li: ({ node, ...props }) => (
                            <li className="text-gray-800 text-sm" {...props} />
                          ),
                          code: ({ node, inline, ...props }) =>
                            inline ? (
                              <code className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-mono" {...props} />
                            ) : (
                              <code className="block bg-gray-100 p-2 rounded text-xs font-mono my-2" {...props} />
                            )
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                      {(message.tokens || message.cached) && (
                        <div className="text-xs text-gray-500 mt-2 pt-2 border-t border-gray-200 flex items-center justify-between">
                          {message.cached ? (
                            <span className="flex items-center gap-1 text-green-600 font-medium">
                              <Sparkles className="w-3 h-3" />
                              Instant (Cached)
                            </span>
                          ) : (
                            <span>{message.tokens} tokens • ${message.cost?.toFixed(4)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm">{message.content}</p>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader className="w-3 h-3 animate-spin" />
                    <span className="text-xs">AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="p-3 border-t border-gray-200 bg-white rounded-b-xl">
            <div className="flex gap-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about compliance results..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none text-xs"
                rows="2"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center min-w-[45px]"
              >
                {isLoading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default AiChatPanel;
